import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, Star, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useAllRestaurantPlaces, toSlug, isFeaturedRestaurant } from './Restaurants';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

// Mandi/Arabian rice copy shown on every Hashtag India location's detail page.
const HASHTAG_INDIA_DESCRIPTION =
  'Discover delicious Mandi and authentic Arabian-style rice dishes, featuring flavorful mandi rice, tender chicken, lamb, and other Middle Eastern and Desi-inspired favorites. A great destination for best mandi, chicken mandi, lamb mandi, Arabian mandi, and flavorful rice dishes for anyone craving authentic, aromatic flavors.';

// Matches every Hashtag India location (Hashtag India, Hashtag India Lewisville, etc.)
const isHashtagIndia = (name: string) => name.trim().toLowerCase().startsWith('hashtag india');

// Desi pizza copy shown on every Pizza Twist location's detail page.
const PIZZA_TWIST_DESCRIPTION =
  'Pizza Twist brings a delicious Desi twist to classic pizza! Enjoy halal pizza, Indian Desi pizza, Pakistani pizza, fusion pizza, and pizza with a twist, along with loaded fries, pasta, and twisted breadsticks. With plant-based and gluten-free options available, there\u2019s something for everyone who loves bold, flavorful pizza.';

// Matches every Pizza Twist location (Pizza Twist, Pizza Twist Lewisville, etc.)
const isPizzaTwist = (name: string) => name.trim().toLowerCase().startsWith('pizza twist');

// Koyla's own marketing copy, shown on its detail page instead of the generic line.
const KOYLA_DESCRIPTION =
  'KOYLA - Indian Smokehouse & Bar. Authentic North Indian flavors with a side of Himalayan taste. Come, experience the timeless smoky aromas and a trip down your memory lane, pure nostalgia!';

// Matches Koyla - Indian Smokehouse & Bar (Allen)
const isKoyla = (name: string) => name.trim().toLowerCase().startsWith('koyla');

// South Indian cuisine copy shown on every South Indian-tagged restaurant's detail page.
const SOUTH_INDIAN_DESCRIPTION =
  'Discover South Indian restaurants in the Dallas-Fort Worth area serving authentic and flavorful dishes from across South India. Explore favorites such as dosa, idli, vada, uttapam, pongal, sambar, rasam, biryani, pulao, South Indian thalis, vegetarian specialties, curries, rice dishes, and traditional snacks and sweets. Find South Indian food in Dallas, Frisco, Plano, Irving, Carrollton, Richardson, McKinney, Allen, and surrounding DFW communities. Whether you\'re looking for crispy dosa, comforting idli and vada, flavorful biryani and pulao, a traditional South Indian thali, or vegetarian Indian food, explore local restaurants and discover South Indian flavors near you.';

// Matches any restaurant tagged "South Indian" in its cuisines list
const isSouthIndian = (cuisines: string[]) =>
  cuisines.some((c) => (c || '').trim().toLowerCase() === 'south indian');

// Every restaurant's public URL is generated automatically from its name + city
// (no manual page creation needed): /restaurants/<name>-<city>
export function restaurantSlug(name: string, city?: string) {
  return `${toSlug(name)}-${toSlug(city || '')}`.replace(/-+$/, '');
}

export default function RestaurantDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { items, loading } = useAllRestaurantPlaces();

  const normalizedSlug = (slug || '').replace(/-+$/, '');
  const restaurant =
    items.find((r) => restaurantSlug(r.name, r.city || r.location) === normalizedSlug) ||
    // Fallback: match on the name portion alone (handles listings with no city
    // on record, whose URL has no city suffix).
    items.find((r) => toSlug(r.name) === normalizedSlug) ||
    // Fallback: the card that navigated here passes its full data via router
    // state, so the page still renders even if the live Places fetch on this
    // page comes back without that listing.
    ((routerLocation.state as any)?.restaurant as typeof items[number] | undefined);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <SEO title="Restaurant Not Found | Dallas Desi Scoop" description="This restaurant listing could not be found." canonical="/restaurants" noindex />
        <p className="text-gray-500 text-lg mb-4">Restaurant not found.</p>
        <button onClick={() => navigate('/restaurants')} className="text-orange-500 font-semibold hover:underline">
          ← Back to Restaurants
        </button>
      </div>
    );
  }

  const city = restaurant.city || restaurant.location || 'Dallas';
  const cuisines = restaurant.cuisines || [];
  const title = `${restaurant.name} in ${city}, TX | Menu, Reviews & Contact | Dallas Desi Scoop`;
  const hashtagIndia = isHashtagIndia(restaurant.name);
  const pizzaTwist = isPizzaTwist(restaurant.name);
  const southIndian = isSouthIndian(cuisines);
  const koyla = isKoyla(restaurant.name);
  const specialDescription = hashtagIndia
    ? HASHTAG_INDIA_DESCRIPTION
    : pizzaTwist
      ? PIZZA_TWIST_DESCRIPTION
      : southIndian
        ? SOUTH_INDIAN_DESCRIPTION
        : undefined;
  const description = koyla
    ? KOYLA_DESCRIPTION
    : specialDescription
      ? `${restaurant.name} in ${city}, TX. ${specialDescription}`
      : `${restaurant.name} is a ${cuisines.join(', ') || 'South Asian'} restaurant in ${city}, TX. Find address, phone number, website, hours, and reviews.`;
  const canonicalPath = `/restaurants/${slug}`;

  const jsonLd = [
    {
      '@type': 'Restaurant',
      name: restaurant.name,
      image: restaurant.image,
      description: koyla ? KOYLA_DESCRIPTION : specialDescription,
      servesCuisine: cuisines,
      address: {
        '@type': 'PostalAddress',
        streetAddress: restaurant.address,
        addressLocality: city,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: restaurant.phone || undefined,
      url: restaurant.website || `${BASE_URL}${canonicalPath}`,
      aggregateRating: restaurant.rating
        ? {
            '@type': 'AggregateRating',
            ratingValue: restaurant.rating,
            reviewCount: restaurant.reviewCount || 1,
          }
        : undefined,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Restaurants', item: `${BASE_URL}/restaurants` },
        { '@type': 'ListItem', position: 3, name: restaurant.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO title={title} description={description} canonical={canonicalPath} ogImage={restaurant.image} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${restaurant.image}")` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)' }} />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.button
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
            onClick={() => navigate('/restaurants')}
            className="self-start flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium mb-5 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Restaurants
            <ChevronRight className="w-3 h-3 opacity-60" />
            <span className="text-white/60">{restaurant.name}</span>
          </motion.button>

          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {restaurant.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <MapPin className="w-3.5 h-3.5 text-orange-400" />
              {city}, TX
            </span>
            {cuisines.length > 0 && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">{cuisines.join(', ')}</span>
              </>
            )}
            {restaurant.rating > 0 && (
              <>
                <span className="text-white/40">·</span>
                <span className="flex items-center gap-1 text-white text-[13px] font-semibold">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  {restaurant.rating} ({restaurant.reviewCount})
                </span>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {restaurant.name}</h2>
          {koyla ? (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {KOYLA_DESCRIPTION}
            </p>
          ) : (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {restaurant.name} serves {cuisines.length > 0 ? cuisines.join(', ') : 'South Asian'} cuisine in {city}, Texas.
              {restaurant.address ? ` Located at ${restaurant.address}.` : ''}
            </p>
          )}
          {specialDescription && (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch] mt-3">
              {specialDescription}
            </p>
          )}
          {/* South Indian copy also appears on South Indian-tagged listings that
              carry their own name-specific description (e.g. Hashtag India), so
              every South Indian card gets it without dropping the Mandi copy. */}
          {southIndian && specialDescription !== SOUTH_INDIAN_DESCRIPTION && (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch] mt-3">
              {SOUTH_INDIAN_DESCRIPTION}
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5 h-fit" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">Contact & Info</h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
              <span>{restaurant.address || '[Coming soon]'}</span>
            </div>
            {restaurant.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <a href={`tel:${restaurant.phone}`} className="hover:underline">{restaurant.phone}</a>
              </div>
            )}
            {restaurant.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="hover:underline break-all">
                  Visit Website
                </a>
              </div>
            )}
          </div>
          {(isFeaturedRestaurant(restaurant) || countSocialLinks(restaurant) > 0) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {isFeaturedRestaurant(restaurant) && (
                <p className="text-[11px] font-semibold text-green-600 tracking-wide mb-2">
                  Featured by TastebudsByAnubhi
                </p>
              )}
              <SocialLinks item={restaurant} px={28} />
            </div>
          )}
          {restaurant.mapsUrl && (
            <a
              href={restaurant.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 block text-center bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              Get Directions
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
